import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { authorizeUrl, useConnections, useCreateConnection, useRevokeConnection, useSetCredentials, useSyncRuns, type Connection } from "../api/hooks";
import { Button, Chip, Empty, ErrorBox, Field, Loading, Page, Table, statusTone } from "../components/ui";
import { CONNECTORS, metaFor } from "../lib/connectors";
import { fmtDateTime, relative, title } from "../lib/format";

export const Route = createFileRoute("/connections")({ component: Connections });

/** Setup order: people first, then the tools that generate the work signals. */
const FAMILIES = ["HRIS", "Engineering", "Chat", "Calendar", "Meetings", "Sales", "Customer", "Identity"] as const;

function Connections() {
  const list = useConnections();
  const [open, setOpen] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const connected = new Set(list.data?.filter((c) => c.status !== "revoked").map((c) => c.connector));
  const available = CONNECTORS.filter((c) => !connected.has(c.id));
  return (
    <Page title="Integrations" sub="Connected apps, their health and what each one is allowed to read. Credentials are encrypted per tenant and never shown.">
      {list.isError && <ErrorBox error={list.error} />}
      {error != null && <div className="mb-3"><ErrorBox error={error} /></div>}
      {list.isPending ? (
        <Loading />
      ) : (list.data?.length ?? 0) === 0 ? (
        <Empty title="Nothing connected yet" body="Pick an app below. You will be sent to the vendor to approve read-only access." />
      ) : (
        <Table
          rows={list.data!}
          keyOf={(c) => c.id}
          cols={[
            { h: "App", cell: (c) => <><span className="font-semibold">{metaFor(c.connector).name}</span><span className="sub">{metaFor(c.connector).family}{c.external_workspace_id ? ` · ${c.external_workspace_id}` : ""}</span></> },
            { h: "Status", cell: (c) => <Chip tone={statusTone(c.status)}>{title(c.status)}</Chip>, w: "120px" },
            { h: "Credentials", cell: (c) => (c.has_credentials ? <span className="text-muted">stored, encrypted</span> : <span className="text-muted">none</span>), w: "150px" },
            { h: "Connected", cell: (c) => <span className="text-muted">{relative(c.updated_at)}</span>, w: "120px" },
            { h: "", cell: (c) => <Row c={c} open={open === c.id} onToggle={() => setOpen(open === c.id ? null : c.id)} onError={setError} />, w: "340px" },
          ]}
        />
      )}
      {open && list.data?.find((c) => c.id === open) && <Runs id={open} />}
      {available.length > 0 && (
        <>
          <h3 className="text-[15px] font-bold mt-8 mb-3">Available</h3>
          <div className="grid grid-cols-3 gap-3">
            {[...available]
              .sort((a, b) => FAMILIES.indexOf(a.family) - FAMILIES.indexOf(b.family))
              .map((c) => (
                <AddCard key={c.id} id={c.id} onError={setError} />
              ))}
          </div>
        </>
      )}
    </Page>
  );
}

function Row({ c, open, onToggle, onError }: { c: Connection; open: boolean; onToggle: () => void; onError: (e: unknown) => void }) {
  const revoke = useRevokeConnection();
  const meta = metaFor(c.connector);
  const [busy, setBusy] = useState(false);
  const connect = async () => {
    setBusy(true);
    try {
      window.location.assign(await authorizeUrl(c.id));
    } catch (e) {
      onError(e);
      setBusy(false);
    }
  };
  return (
    <div className="flex flex-nowrap gap-2 justify-end">
      {c.status !== "revoked" && meta.auth !== "api_key" && (
        <Button variant="light" className="!py-1.5 !px-3 !text-[12px]" disabled={busy} onClick={() => void connect()}>
          {c.status === "active" ? "Re-authorise" : "Connect →"}
        </Button>
      )}
      {c.status !== "revoked" && meta.auth === "api_key" && <KeyForm id={c.id} onError={onError} />}
      <Button variant="light" className="!py-1.5 !px-3 !text-[12px]" onClick={onToggle}>{open ? "Hide runs" : "Sync runs"}</Button>
      {c.status !== "revoked" && (
        <Button variant="light" className="!py-1.5 !px-3 !text-[12px] !text-red" disabled={revoke.isPending} onClick={() => { if (window.confirm(`Disconnect ${meta.name}? Syncing stops immediately.`)) revoke.mutate(c.id, { onError }); }}>
          Disconnect
        </Button>
      )}
    </div>
  );
}

function KeyForm({ id, onError }: { id: string; onError: (e: unknown) => void }) {
  const set = useSetCredentials();
  const [key, setKey] = useState("");
  return (
    <form
      className="flex gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        set.mutate({ id, api_key: key }, { onError, onSuccess: () => setKey("") });
      }}
    >
      <input className="input !w-[180px] !py-1.5 !text-[12px]" type="password" placeholder="API key" value={key} onChange={(e) => setKey(e.target.value)} autoComplete="off" />
      <Button className="!py-1.5 !px-3 !text-[12px]" disabled={!key || set.isPending} type="submit">Verify &amp; save</Button>
    </form>
  );
}

function AddCard({ id, onError }: { id: string; onError: (e: unknown) => void }) {
  const meta = metaFor(id);
  const create = useCreateConnection();
  const [workspace, setWorkspace] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      const c = await create.mutateAsync({ connector: id, external_workspace_id: meta.workspace ? workspace : null });
      if (meta.auth !== "api_key") window.location.assign(await authorizeUrl(c.id));
    } catch (e) {
      onError(e);
    } finally {
      setBusy(false);
    }
  };
  // Same shape in every tile: icon + name, the two-line promise, then the action pinned
  // to the bottom at full width. Text length no longer decides where the button lands.
  return (
    <div className="card flex flex-col gap-3">
      <div className="flex items-center gap-3">
        <span className="tile-icon" aria-hidden>{meta.name.slice(0, 1)}</span>
        <span className="font-bold text-[14px] flex-1">{meta.name}</span>
        <span className="text-[11px] text-text-4 font-bold uppercase tracking-wide">{meta.family}</span>
      </div>
      <p className="text-[12px] text-muted leading-relaxed"><b className="text-ink font-semibold">Reads</b> {meta.reads}. <b className="text-ink font-semibold">Never</b> {meta.never}.</p>
      {meta.note && <p className="text-[12px] text-muted leading-relaxed">{meta.note}</p>}
      {meta.workspace && (
        <Field label={meta.workspace.label}>
          <input className="input" placeholder={meta.workspace.placeholder} value={workspace} onChange={(e) => setWorkspace(e.target.value)} />
        </Field>
      )}
      <Button variant="light" className="w-full justify-center mt-auto" disabled={busy || (!!meta.workspace && !workspace)} onClick={() => void go()}>
        Connect →
      </Button>
    </div>
  );
}

function Runs({ id }: { id: string }) {
  const runs = useSyncRuns(id);
  if (runs.isPending) return <Loading />;
  if (runs.isError) return <ErrorBox error={runs.error} />;
  return (
    <div className="mt-3">
      <Table
        rows={runs.data}
        keyOf={(r) => r.id}
        empty="No sync runs yet — the scheduler picks new connections up within 15 minutes"
        cols={[
          { h: "Started", cell: (r) => fmtDateTime(r.started_at), w: "160px" },
          { h: "Object", cell: (r) => r.object_type, w: "140px" },
          { h: "Status", cell: (r) => <Chip tone={statusTone(r.status)}>{title(r.status)}</Chip>, w: "140px" },
          { h: "Pages", cell: (r) => r.pages, w: "80px" },
          { h: "Records", cell: (r) => r.records_ingested, w: "90px" },
          { h: "Rate-limited", cell: (r) => (r.rate_limited_seconds ? `${r.rate_limited_seconds}s` : "—"), w: "110px" },
          { h: "Error", cell: (r) => <span className="text-muted">{r.error ?? ""}</span> },
        ]}
      />
    </div>
  );
}
